/* eslint-disable no-underscore-dangle */
/* eslint-disable import/prefer-default-export */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import fetchCachedCMSDataRequest from 'network/fetchCachedCMSData';
import remoteTimeNow from 'libs/remote-time-now';
import { selectAvailableLocales } from 'models/localization';
import { CMSEvent, eventEntity } from '../events/types';
import {
  Booth,
  boothEntity,
  BoothFetchingState,
  BoothFetchedLevel,
} from '../booths/types';
import { BoothTaxonomy, boothTaxonomyEntity } from '../boothTaxonomies/types';
import { BoothTier, boothTierEntity } from '../boothTiers/types';

const urlRegex = /<p>(.*)<\/p>/;

export const getBoothExtraFields = (tab) => {
  let livestreams: any;
  let videoCallUrl: string;
  let hasAttachment: boolean;
  if ((tab.body || []).length) {
    if (tab.title) {
      if (Object.values(tab.title).some((value) => value === 'Live Stream(s)')) {
        const content = tab.body[0]?.answer?.contentRichtextHtml;
        if (content && Object.values(content).length) {
          livestreams = [{
            tabId: tab.id,
          }];
        }
      } else if (Object.values(tab.title).some((value) => value === 'Video Call')) {
        const content: { [key: string]: string } = tab.body[0]?.answer?.contentRichtextHtml;
        if (content && Object.values(content).length) {
          [, videoCallUrl] = Object.values(content)[0].match(urlRegex);
        }
      }
    }

    hasAttachment = tab.body.some((element) => (
      tab?.masterElement?.masterControlledBody === false
          || (
            element.__typename === 'BoothFileContentElement'
              && element.file
              && Object.values(element.file).some((file) => file)
          )
    ));
  }

  return {
    livestreams,
    videoCallUrl,
    hasAttachment,
  };
};

export const fetchCachedCMSData = createAsyncThunk(
  'cms/fetchCachedCMSData',
  async (_, { getState }) => {
    const { eventConfigs } = (getState() as any);
    const cmsCachedDataFileName = eventConfigs?.cmsCachedDataFileName;
    const locales = selectAvailableLocales(getState());
    const { data: { eventByDomain: data } } = await fetchCachedCMSDataRequest(cmsCachedDataFileName);

    const {
      halls,
      boothTaxonomies: boothTaxonomiesData,
      boothTiers: boothTiersData,
    } = data;
    delete data.halls;
    delete data.boothTaxonomies;
    delete data.boothTiers;

    const lastReceivedAt = remoteTimeNow().add(7, 'days').toISOString();
    const boothsNodes = halls.nodes
      .flatMap((it: any) => it.booths.nodes)
      .map((it: any) => ({
        ...it,
        $fetchingState: BoothFetchingState.Fulfilled,
        $fetchedLevel: [BoothFetchedLevel.Meta, BoothFetchedLevel.Design, BoothFetchedLevel.Details]
          .map((level) => ({ level, locales })),
        $lastReceivedAt: lastReceivedAt,
      }));
    const boothIds = boothsNodes.map((it: any) => it.id);
    const { events } = normalize<CMSEvent>(data, eventEntity).entities;
    const { booths, boothTabs } = normalize<Booth>(boothsNodes, [boothEntity]).entities;
    if (!Object.values(boothTabs).filter((tab) => tab.id).length) {
      // Booth tabs' ids may be missing in some event cached data. If so, then
      // SPA should fetch booth details from CMS to make sure booth tabs data
      // can be stored in redux correctly.
      Object.keys(booths).forEach((boothId) => {
        booths[boothId].$fetchedLevel = [BoothFetchedLevel.Meta, BoothFetchedLevel.Design].map((level) => ({ level, locales }));
      });
      Object.keys(boothTabs).forEach((key) => delete boothTabs[key]);
    }
    const { boothTaxonomies } = normalize<BoothTaxonomy>(
      boothTaxonomiesData.nodes,
      [boothTaxonomyEntity],
    ).entities;
    const { boothTiers } = normalize<BoothTier>(
      boothTiersData.nodes,
      [boothTierEntity],
    ).entities;

    Object.values(booths).forEach((booth) => {
      booth?.rootContentElement?.tabs?.forEach((tabId) => {
        const tab = boothTabs[tabId];
        if (!tab) return;
        const {
          livestreams,
          videoCallUrl,
          hasAttachment,
        } = getBoothExtraFields(tab);
        if (livestreams) booth.livestreams = livestreams;
        if (videoCallUrl) booth.videoCallUrl = videoCallUrl;
        if (hasAttachment) booth.hasAttachment = hasAttachment;
      });
    });

    return {
      events,
      booths,
      boothTabs,
      boothTaxonomies,
      boothTiers,
      boothIds,
    };
  },
);
